/**
 * A aritmética de handicap do flight, no cliente.
 *
 * Espelha `ScoreDifferentialService` e `GroupScoringService` do backend — não porque duplicar seja
 * bom, mas porque o scorecard em grupo (canvas 1s) precisa mostrar o net **ao vivo**, entre um
 * toque no stepper e o salvamento. Ir ao servidor a cada toque colocaria a rede no caminho do
 * número que o jogador está lendo enquanto marca.
 *
 * O servidor continua sendo a autoridade: é ele que devolve `standings` a cada buraco salvo, e é o
 * valor dele que a tela exibe assim que chega. Estas funções são a previsão até lá.
 *
 * Fonte das regras:
 *   backend/src/modules/rounds/domain/services/score-differential.service.ts
 *   backend/src/modules/group-rounds/domain/services/group-scoring.service.ts
 */

import type {
  CourseDetail,
  GroupParticipant,
  GroupPlayerHole,
  GroupRound,
} from '@/lib/api/types';

/**
 * O snapshot do campo que a volta **vai** receber, calculado antes de ela existir.
 *
 * Espelha `StartRoundUseCase`: numa volta dupla (18 num campo de 9) o rating é a média dos dois
 * tees e o par dobra; numa volta de 9 num campo de 18, o par é o dos nove primeiros buracos.
 * Usar `parTotal` direto daria o número certo só no caso mais comum, e erraria em silêncio nos
 * outros dois — justamente onde o jogador não teria como desconfiar.
 */
export function courseSnapshotFor(
  course: CourseDetail,
  teeBoxName: string,
  teeBoxNameBack: string | null,
  holesPlayed: 9 | 18,
): { courseRating: number; slopeRating: number; par: number } | null {
  const front = course.teeBoxes.find((t) => t.name === teeBoxName);
  if (!front) return null;

  const isDouble = holesPlayed > course.totalHoles;
  const back = teeBoxNameBack ? course.teeBoxes.find((t) => t.name === teeBoxNameBack) : undefined;

  const courseRating =
    isDouble && back ? round1((front.courseRating + back.courseRating) / 2) : front.courseRating;
  const slopeRating =
    isDouble && back
      ? Math.round((front.slopeRating + back.slopeRating) / 2)
      : front.slopeRating;

  return { courseRating, slopeRating, par: roundParFor(course, holesPlayed) };
}

function roundParFor(course: CourseDetail, holesPlayed: 9 | 18): number {
  if (holesPlayed === course.totalHoles) return course.parTotal;
  if (holesPlayed === 9) {
    return course.holes.filter((h) => h.number <= 9).reduce((sum, h) => sum + h.par, 0);
  }
  if (holesPlayed === 18 && course.totalHoles === 9) return course.parTotal * 2;
  return course.parTotal;
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Course handicap = HI × (slope / 113) + (CR − par), arredondado. `null` sem índice. */
export function courseHandicapOf(
  handicapIndex: number | null,
  slopeRating: number,
  courseRating: number,
  coursePar: number,
): number | null {
  if (handicapIndex === null) return null;
  return Math.round(handicapIndex * (slopeRating / 113) + (courseRating - coursePar));
}

/** Total de tacadas recebidas na volta — é o course handicap, nunca negativo. */
export function strokesReceivedTotal(courseHandicap: number): number {
  return Math.max(0, Math.floor(courseHandicap));
}

/**
 * Tacadas recebidas **neste** buraco, distribuídas por stroke index: uma por buraco em ordem de
 * dificuldade, dando a volta quando o handicap passa do número de buracos.
 */
export function strokesReceivedOnHole(
  courseHandicap: number | null,
  strokeIndex: number,
  totalHoles: 9 | 18,
): number {
  if (courseHandicap === null) return 0;
  const ch = strokesReceivedTotal(courseHandicap);
  return Math.floor(ch / totalHoles) + (strokeIndex <= ch % totalHoles ? 1 : 0);
}

/** Pontos stableford do buraco: 2 no net par, com piso em zero. */
export function stablefordPointsForHole(
  strokes: number,
  par: number,
  strokesReceived: number,
): number {
  return Math.max(0, 2 - (strokes - strokesReceived - par));
}


/**
 * O buraco de um jogador, venha de onde vier.
 *
 * A fonte deixou de ser uma só quando o amigo passou a poder entrar na volta: quem tem `Round`
 * própria guarda o buraco nela, e quem não tem — o convidado sem conta — guarda no participante.
 * O servidor resolve os dois lados e entrega tudo em `scoresByParticipant`, então a tela lê um
 * mapa só e não precisa saber de qual lado veio.
 */
export function holeOf(
  group: GroupRound,
  participant: Pick<GroupParticipant, 'id'>,
  holeNumber: number,
): GroupPlayerHole | null {
  return (
    group.scoresByParticipant[participant.id]?.find((s) => s.holeNumber === holeNumber) ?? null
  );
}

/**
 * Quem você pode marcar — a regra de "quem edita quem" do canvas 2b.
 *
 * O dono é o marcador do flight e escreve qualquer linha, que é o papel que existe no golfe de
 * verdade. Quem entrou depois escreve **só a sua**: são as tacadas dele, e duas pessoas com
 * caneta na mesma linha é como um cartão perde a autoridade. O servidor recusa o resto de
 * qualquer jeito; aqui é só não oferecer o campo.
 */
export function scorableParticipants(group: GroupRound): GroupParticipant[] {
  if (group.viewer.canScoreAll) return group.participants;
  const id = group.viewer.participantId;
  if (!id || !group.viewer.joined) return [];
  return group.participants.filter((p) => p.id === id);
}
